import { isPlatformServer } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { RecipeSearchResponseMapped, RecipeService, RecipeStampLocal } from '@woolworthsnz/recipe';
import { isDefined, StatefulService } from '@woolworthsnz/styleguide';
import {
	RecipeCategoryResponse,
	RecipeListRequest,
	RecipeSearchRequest,
	SavedRecipesResponse,
	SortOptionItem,
} from '@woolworthsnz/trader-api';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

export interface RecipesState {
	currentPage: number;
	currentSortOption?: RecipeListRequest.SortEnum;
	category?: RecipeCategoryResponse;
	sortOptions?: SortOptionItem[];
	loading?: boolean;
	newPageLoading?: boolean;
	visibleRecipes: RecipeStampLocal[];
	recipes: RecipeStampLocal[];
	recipeColumns?: number;
	savedRecipes: number[];
	totalRecipes: number;
	searchQuery?: string;
	searchTarget?: RecipeSearchRequest.TargetEnum;
}

const initialState: RecipesState = {
	currentPage: 1,
	currentSortOption: undefined,
	category: undefined,
	sortOptions: [],
	loading: true,
	newPageLoading: true,
	recipes: [],
	visibleRecipes: [],
	savedRecipes: [],
	totalRecipes: 0,
	searchQuery: undefined,
	searchTarget: undefined,
};

export enum RecipePageType {
	search = 'search',
	category = 'category',
}

@Injectable({ providedIn: 'root' })
export class RecipesStoreService extends StatefulService<RecipesState> {
	constructor(private recipeService: RecipeService, @Inject(PLATFORM_ID) private platformId: object) {
		super(initialState);

		if (isPlatformServer(this.platformId)) {
			this.setState({ recipeColumns: 5 });
		}
	}

	public loadCategoryRecipes(categoryId: number, sortOption?: RecipeListRequest.SortEnum): Observable<void> {
		this.setState({ ...initialState, currentSortOption: this.state.currentSortOption });
		return forkJoin([
			this.recipeService.getRecipesForCategory(categoryId, { sort: sortOption }).pipe(
				tap((result) => {
					this.setState({
						currentSortOption: result.sortOptions?.find((o) => !!o.selected)?.value as RecipeListRequest.SortEnum,
						recipes: result.recipes,
						visibleRecipes: this.getVisibleRecipes(result.recipes, result.totalRecipes),
						totalRecipes: result.totalRecipes,
						sortOptions: result.sortOptions,
					});
				})
			),
			this.recipeService.getCategoryById(categoryId).pipe(
				tap((result) =>
					this.setState({
						category: result,
					})
				)
			),
		]).pipe(
			tap(() => {
				this.setState({ loading: false, newPageLoading: false });
			}),
			map(() => undefined)
		);
	}

	public searchRecipes(
		searchQuery: string,
		sortOption: RecipeSearchRequest.SortEnum,
		searchTarget?: RecipeSearchRequest.TargetEnum
	): Observable<void> {
		this.setState({ ...initialState, currentSortOption: this.state.currentSortOption, searchQuery, searchTarget });

		return this.recipeService.searchRecipes({ search: searchQuery, sort: sortOption, target: searchTarget }).pipe(
			tap((result) =>
				this.setState({
					currentSortOption: result.sortOptions?.find((o) => !!o.selected)?.value as RecipeListRequest.SortEnum,
					recipes: result.recipes,
					visibleRecipes: this.getVisibleRecipes(result.recipes, result.totalRecipes),
					totalRecipes: result.totalRecipes,
					loading: false,
					newPageLoading: false,
					sortOptions: result.sortOptions,
				})
			),
			map(() => undefined)
		);
	}

	public loadMore(type: RecipePageType = RecipePageType.category): Observable<RecipeSearchResponseMapped | undefined> {
		const nextPage = this.state.currentPage + 1;
		const state: RecipesState = this.state;

		this.setState({ newPageLoading: true });

		let getRecipes: Observable<RecipeSearchResponseMapped>;

		switch (type) {
			case RecipePageType.search:
				getRecipes = this.recipeService.searchRecipes({
					search: state.searchQuery,
					page: nextPage,
					target: state.searchTarget,
					sort: state.currentSortOption,
				});
				break;
			case RecipePageType.category:
			default:
				getRecipes = this.recipeService.getRecipesForCategory(this.state.category?.id || 0, {
					sort: this.state.currentSortOption,
					page: nextPage,
				});
		}

		return getRecipes.pipe(
			tap((result) => {
				const recipes = [...this.state.recipes, ...(result.recipes || [])];
				this.setState({
					newPageLoading: false,
					currentPage: nextPage,
					recipes,
					visibleRecipes: this.getVisibleRecipes(recipes, result.totalRecipes),
					totalRecipes: result.totalRecipes,
				});
			}),
			map(() => undefined)
		);
	}

	public setRecipeColumns(cols: number): void {
		this.setState({
			recipeColumns: cols,
		});
		this.setState({
			visibleRecipes: this.getVisibleRecipes(this.state.recipes, this.state.totalRecipes),
		});
	}

	public sortChange(
		sort: RecipeListRequest.SortEnum,
		type: RecipePageType = RecipePageType.category
	): Observable<RecipeSearchResponseMapped> {
		const state: RecipesState = this.state;
		const page = 1; // should reset to first page when resorting

		let getRecipes: Observable<RecipeSearchResponseMapped>;

		switch (type) {
			case RecipePageType.search:
				getRecipes = this.recipeService.searchRecipes({
					search: state.searchQuery,
					page,
					target: state.searchTarget,
					sort: sort,
				});
				break;
			case RecipePageType.category:
			default:
				getRecipes = this.recipeService.getRecipesForCategory(state.category?.id || 0, { sort });
		}

		return getRecipes.pipe(
			tap((result) => {
				this.setState({
					currentPage: page,
					currentSortOption: sort,
					recipes: result.recipes || [],
					visibleRecipes: this.getVisibleRecipes(result.recipes, result.totalRecipes),
					totalRecipes: result.totalRecipes,
				});
			})
		);
	}

	public loadSavedList(): Observable<SavedRecipesResponse> {
		return this.recipeService.getSavedRecipes().pipe(
			tap((result) =>
				this.setState({
					savedRecipes: result.savedRecipes?.map((r) => r.recipeId).filter(isDefined) || [],
				})
			),
			catchError(
				() =>
					// fail silently
					EMPTY
			)
		);
	}

	public saveRecipe(recipeId: number): Observable<HttpResponse<any>> {
		// remove from favourite if exists
		if (this.state.savedRecipes.indexOf(recipeId) > -1) {
			return this.recipeService.removeSavedRecipe(recipeId).pipe(
				tap(() => {
					this.setState({
						savedRecipes: [...this.state.savedRecipes.filter((r: number) => r !== recipeId)],
					});
				})
			);
		}

		// save as favourite
		return this.recipeService
			.saveRecipe(recipeId)
			.pipe(tap(() => this.setState({ savedRecipes: [...this.state.savedRecipes, recipeId] })));
	}

	private getVisibleRecipes(recipes?: RecipeStampLocal[], totalRecipes?: number): RecipeStampLocal[] | undefined {
		const cols = this.state.recipeColumns;
		if (!recipes || !cols || cols === 0 || recipes.length === totalRecipes) {
			return recipes;
		}
		const copyRecipes = [...recipes];

		const mod = recipes.length % cols;
		if (mod) {
			copyRecipes.splice(-mod);
		}
		return copyRecipes;
	}
}
